import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  RocketLaunch,
  DeveloperMode,
} from '@mui/icons-material';
import { authService } from '../services/auth.service';
import { environmentService, Environment } from '../config/environment';
import logoLight from '../assets/logos/logo_light.png';
import logoDark from '../assets/logos/logo_dark.png';

const Login: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [environment] = useState<Environment>(
    environmentService.getCurrentEnvironment()
  );


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Access denied. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'light'
          ? `radial-gradient(circle at 2% 10%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 40%), 
             radial-gradient(circle at 98% 90%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 40%),
             #f9fafb`
          : `radial-gradient(circle at 2% 10%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 40%), 
             radial-gradient(circle at 98% 90%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 40%),
             #0a0b10`,
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              height: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 0.5,
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'light' ? logoLight : logoDark}
              alt="Teachora Logo"
              sx={{
                height: '100%',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -1, mb: 1 }}>
            Teachora Admin
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
            Management Portal Access
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
            boxShadow: theme.palette.mode === 'light'
              ? '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
              : '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.5)',
          }}
        >

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 600, fontSize: '0.8rem' } }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailOutlined fontSize="small" color="action" /></InputAdornment>,
                  sx: { borderRadius: 3 }
                }}
              />
              <TextField
                fullWidth
                label="Security password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" color="action" /></InputAdornment>,
                  sx: { borderRadius: 3 }
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 800,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Enter Dashboard'}
              </Button>
            </Stack>
          </form>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {environment === 'dev' ? (
              <Chip icon={<DeveloperMode fontSize="small" />} label="Development Build" size="small" variant="outlined" sx={{ borderStyle: 'dashed', opacity: 0.7 }} />
            ) : (
              <Chip icon={<RocketLaunch fontSize="small" />} label="Protected Production" size="small" variant="outlined" sx={{ borderStyle: 'solid', opacity: 0.7 }} />
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
            © {new Date().getFullYear()} Teachora Admin. Authorised Personnel Only.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
